import { Printer, Bluetooth, Usb, ChevronLeft, CheckCircle2, Sparkles } from "lucide-react";
import { useLocation, Link } from "wouter";
import { useData } from "@/contexts/DataContext";

const SUPPORTED_PRINTERS = [
  {
    brand: "Bixolon",
    models: [
      {
        name: "SRP-350V",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
    ],
  },
  {
    brand: "DCode",
    models: [
      {
        name: "DC-RP 20",
        type: '2" Receipt Printer',
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: true,
      },
      {
        name: "DC-MP 20",
        type: '2" Receipt Printer',
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
      {
        name: "DC-MP 21",
        type: '2" Receipt Printer',
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: true,
      },
      {
        name: "DC-MP-2M",
        type: '2" Receipt Printer',
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: true,
      },
      {
        name: "DC-3M",
        type: '3" Receipt Printer',
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: true,
      },
      {
        name: "DC-RP-30-UB",
        type: '3" Receipt Printer',
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: true,
      },
      {
        name: "DC-3R-1",
        type: '3" Receipt Printer',
        paperWidth: "58mm",
        connectivity: ["USB"],
        tested: true,
      },
      {
        name: "DC-MP 30",
        type: '3" Receipt Printer',
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
    ],
  },
  {
    brand: "Epson",
    models: [
      {
        name: "TM-T82X",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB"],
        tested: true,
      },
      {
        name: "TM-M30III",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
      {
        name: "TM-T88VII",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB"],
        tested: false,
      },
      {
        name: "TM-T88VI",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB"],
        tested: false,
      },
    ],
  },
  {
    brand: "iDPRT",
    models: [
      {
        name: "SP420",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
    ],
  },
  {
    brand: "Rongta",
    models: [
      {
        name: "RP58E",
        type: "Thermal Receipt Printer",
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
      {
        name: "RPP02N",
        type: "Mobile Receipt Printer",
        paperWidth: "58mm",
        connectivity: ["Bluetooth"],
        tested: false,
      },
    ],
  },
  {
    brand: "Sewoo",
    models: [
      {
        name: "LK-T12",
        type: "Thermal Receipt Printer",
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
      {
        name: "LK-T21",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
    ],
  },
  {
    brand: "Star",
    models: [
      {
        name: "TSP143IIIU",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
      {
        name: "SM-S230i",
        type: "Mobile Receipt Printer",
        paperWidth: "58mm",
        connectivity: ["Bluetooth"],
        tested: false,
      },
    ],
  },
  {
    brand: "TVS",
    models: [
      {
        name: "RP 45 Shoppe",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
      {
        name: "RP-3160 Star",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
    ],
  },
  {
    brand: "Xprinter",
    models: [
      {
        name: "XP-58IIL",
        type: "Thermal Receipt Printer",
        paperWidth: "58mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
      {
        name: "XP-80C",
        type: "Thermal Receipt Printer",
        paperWidth: "80mm",
        connectivity: ["USB", "Bluetooth"],
        tested: false,
      },
    ],
  },
];

export default function SupportedPrinters() {
  const [, setLocation] = useLocation();
  const { isUltimateOwnerMobile } = useData();

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      {isUltimateOwnerMobile ? (
        <div className="px-4 pb-2" style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)" }}>
          <div className="flex items-start gap-3">
            <button onClick={handleBack} className="rounded-full p-2 border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 mt-1 shrink-0">
              <ChevronLeft className="h-4 w-4 text-[#0F172A] dark:text-[#EEEAE3]" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-black uppercase tracking-tighter leading-[0.95] text-[#0F172A] dark:text-[#EEEAE3]">
                VERIFIED<br /><span className="text-[#8B1A1A]">HARDWARE LIST</span>
              </h1>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-[#8A8A92] mt-2">
                Browse verified thermal printers
              </p>
            </div>
          </div>
        </div>
      ) : (
        <header className="px-4 py-4 flex items-center sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md">
          <button onClick={handleBack} className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform shrink-0">
            <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
          </button>
          <h1 className="text-lg font-black text-slate-900 dark:text-white ml-4 tracking-tight">Supported Printers</h1>
        </header>
      )}

      <div className="flex-1 px-4 py-4 pb-24 overflow-y-auto">

        {/* Info Banner — premium dark glass card on Ultimate */}
        {isUltimateOwnerMobile ? (
          <div className="rounded-3xl p-5 mb-6 border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60 backdrop-blur-xl flex items-start gap-4">
            <div className="rounded-xl p-2.5 bg-[#D4AF37]/15 border border-[#D4AF37]/30 shrink-0">
              <Sparkles className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-1">
                Universal Compatibility
              </p>
              <p className="text-xs text-slate-500 dark:text-[#8A8A92] leading-relaxed">
                Printer not listed? DailyHisaab works with <span className="font-semibold text-[#0F172A] dark:text-[#EEEAE3]">any standard ESC/POS</span> thermal printer via USB or Bluetooth.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm mb-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-950/30 flex items-center justify-center shrink-0">
              <Printer className="h-6 w-6 text-[#8B2121] dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-white">Universal Compatibility</h2>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                DailyHisaab works seamlessly with any standard ESC/POS thermal receipt printer, even if it is not explicitly listed below.
              </p>
            </div>
          </div>
        )}

        {/* Brand sections */}
        <div className="flex flex-col gap-6">
          {SUPPORTED_PRINTERS.map((group) => (
            <div key={group.brand}>
              {/* Brand heading */}
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="text-xs font-black text-[#8B2121] dark:text-red-400 uppercase tracking-widest">
                  {group.brand}
                </span>
                <div className="flex-1 h-px bg-[#8B2121]/10 dark:bg-white/10" />
              </div>

              {/* Models List */}
              <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl px-4 py-2 shadow-sm">
                <div className="flex flex-col">
                  {group.models.map((model, idx) => (
                    <div key={model.name}>
                      <div className="flex items-start justify-between gap-2 flex-wrap py-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm text-slate-900 dark:text-white">
                              {model.name}
                            </span>

                            {/* Premium Verified Badge Redesign */}
                            {model.tested && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-black px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-sm shadow-emerald-500/20 tracking-wide uppercase">
                                <CheckCircle2 className="w-3 h-3" /> Verified
                              </span>
                            )}

                          </div>
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 font-medium">
                            {model.type} • {model.paperWidth}
                          </p>
                        </div>

                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          {model.connectivity.map((c) => (
                            <span
                              key={c}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-lg ${c === "Bluetooth"
                                ? "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400"
                                : "bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400"
                                }`}
                            >
                              {c === "Bluetooth" ? (
                                <Bluetooth className="h-3 w-3" />
                              ) : (
                                <Usb className="h-3 w-3" />
                              )}
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Divider */}
                      {idx < group.models.length - 1 && (
                        <div className="border-t border-slate-50 dark:border-slate-800" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Help Footer */}
        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 text-center pb-8">
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
            Is your printer not listed?{" "}
            <Link
              href="/support"
              className="font-bold text-[#8B2121] dark:text-red-400 hover:opacity-80 transition-opacity"
            >
              Need Help?
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
}